import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import React, { useCallback, useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import { useAuth } from 'contexts/auth-context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
// import Link from '@mui/material/Link';
import { Link } from 'react-router-dom';
import { NavPages, NavUtils } from 'utils';

import '../../styles/google-button.css';

interface Props {
    eventAlias?: string;
}

export const Login = (props: Props) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const { login, signInWithGoogle, currentUser } = useAuth();
    const navigate = useNavigate();
    let [searchParams] = useSearchParams();
    const returnUrl = searchParams.get('returnUrl');
    const signupPath = `${NavUtils.generateNavPath(NavPages.Signup, undefined, props.eventAlias)}${
        returnUrl ? `?returnUrl=${returnUrl}` : ''
    }`;

    const handleSubmit = async () => {
        try {
            const response = await login(email, password);
            if (response.user) {
                navigateAfterLogin();
            }
        } catch (error) {
            console.log('Login failed', error);
            setError('Incorrect email or password.');
        }
    };

    const navigateAfterLogin = useCallback(() => {
        const returnUrl = searchParams.get('returnUrl');
        navigate(returnUrl ? decodeURI(returnUrl) : `/${props.eventAlias ?? ''}`);
    }, [navigate, props.eventAlias, searchParams]);

    useEffect(() => {
        if (currentUser) {
            navigateAfterLogin();
        }
    }, [currentUser, navigateAfterLogin]);

    const handleGoogleSignIn = async () => {
        try {
            await signInWithGoogle();
        } catch (error) {
            console.log('Google sign-in failed', error);
            setError('Google sign-in failed. Please try again.');
        }
    };

    const handleReset = () => {
        setEmail('');
        setPassword('');
        setError('');
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmit();
        }
    };

    const wrapperStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        padding: 5,
        alignItems: 'center'
    };

    return (
        <div className="wrapper" style={wrapperStyle}>
            <Stack spacing={2} direction="column">
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                    id="txt-email"
                    label="Email"
                    type="email"
                    value={email}
                    onKeyDown={handleKeyPress}
                    onChange={(event) => setEmail(event.target.value)}
                />
                <TextField
                    type="password"
                    id="txt-password"
                    label="Password"
                    value={password}
                    onKeyDown={handleKeyPress}
                    onChange={(event) => setPassword(event.target.value)}
                />
                <Button variant="contained" onClick={handleSubmit}>
                    Login
                </Button>
                <Button variant="contained" onClick={handleReset}>
                    Reset
                </Button>
                <Typography variant="body1" gutterBottom>
                    Don't have an account? <Link to={signupPath}>Sign Up here</Link>
                </Typography>
                <button className="gsi-material-button" onClick={handleGoogleSignIn}>
                    <div className="gsi-material-button-state"></div>
                    <div className="gsi-material-button-content-wrapper">
                        <div className="gsi-material-button-icon">
                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ display: 'block' }}>
                                <path
                                    fill="#EA4335"
                                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                                ></path>
                                <path
                                    fill="#4285F4"
                                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                                ></path>
                                <path
                                    fill="#FBBC05"
                                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                                ></path>
                                <path
                                    fill="#34A853"
                                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                                ></path>
                                <path fill="none" d="M0 0h48v48H0z"></path>
                            </svg>
                        </div>
                        <span className="gsi-material-button-contents">Sign in with Google</span>
                        <span style={{ display: 'none' }}>Sign in with Google</span>
                    </div>
                </button>
            </Stack>
        </div>
    );
};
